import { AssistantChat } from './assistant/AssistantChat';
import React, { useState, useMemo } from 'react';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  BookOpen,
  MessageCircle,
  Inbox,
  Settings,
  Target,
  Calendar,
  Mail,
  ShieldAlert,
  User as UserIcon,
  Package,
  FileText,
  Calculator,
  FileUp,
  Hammer,
  GraduationCap,
  DollarSign,
  Receipt,
  Factory,
  PlayCircle,
  Bot,
  Briefcase
} from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

import { Topbar } from './layout/Topbar';
import { Sidebar, type MenuItem } from './layout/Sidebar';
import { getNicheLabels } from '../utils/nicheUtils';
import { CommandPalette } from './CommandPalette';
import { FloatingChat } from './chat/FloatingChat';
import { FloatingServiceNotes } from './leads/FloatingServiceNotes';
import { isNiche } from '../lib/niche';
import { canUseAiAgent } from '../services/aiAgentService';
import { canSeeTalentBank } from '../services/talentService';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { user, company, isSuperAdmin: initialIsSuperAdmin, permissions } = useAuth();
  const location = useLocation();

  const userRole = user?.role?.toLowerCase().trim() || '';
  const isSuperAdmin = userRole === 'superadmin' || initialIsSuperAdmin;
  const isCS = userRole === 'cs';
  const activeTab = location.pathname === '/' ? 'dashboard' : location.pathname.substring(1);
  // Atalho pra "gestão" (gestor/admin/superadmin/ceo): users.manage é concedida
  // exatamente a essas 4 roles, então serve de proxy sem repetir a lista aqui.
  const can = (code: string) => permissions.includes(code);
  const isManager = can('users.manage');

  // Menu items — cada item pergunta ao catálogo de permissões (mesmo que
  // decide o que a rota deixa abrir), não reimplementa a regra por role.
  const menuItems = useMemo(() => {
    const nicheLabels = getNicheLabels(company?.niche);

    // A ordem do array É a ordem do menu: itens da mesma `group` ficam
    // juntos e o Sidebar desenha o título de cada seção quando o grupo muda.
    // Quem vê o quê continua vindo só das permissões/nicho/módulos abaixo.
    const baseItems: MenuItem[] = [
      // Geral
      { id: 'dashboard', label: nicheLabels.dashboard, icon: LayoutDashboard, color: 'text-blue-600', group: 'Geral' },
      { id: 'calendar', label: 'Agenda', icon: Calendar, color: 'text-indigo-600', group: 'Geral' },

      // Comercial
      { id: 'leads', label: nicheLabels.leads, icon: Target, color: 'text-red-700', group: 'Comercial' },
      ...(can('inbox.access') ? [
        { id: 'inbox', label: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-700', group: 'Comercial' }
      ] : []),
      ...(can('sales_dashboard.access') ? [
        { id: 'sales', label: nicheLabels.sales, icon: TrendingUp, color: 'text-green-700', group: 'Comercial' }
      ] : []),
      ...(canUseAiAgent(user?.role, user?.company_id, user?.id) ? [
        { id: 'atendente-ia', label: 'Atendente de IA', icon: Bot, color: 'text-violet-600', group: 'Comercial' },
      ] : []),
      // O menu do chat interno foi removido para todas as empresas; ele só
      // aparece nas que ligam companies.settings.internal_chat (além do
      // módulo 'chat' em modules_enabled, checado no filtro abaixo).
      ...(company?.settings?.internal_chat === true ? [
        { id: 'chat', label: 'Chat Interno', icon: MessageCircle, color: 'text-pink-600', group: 'Comercial' },
      ] : []),

      ...(can('talent.access') && canSeeTalentBank(company) ? [
        { id: 'banco-talentos', label: 'Banco de Talentos', icon: Briefcase, color: 'text-teal-600', group: 'Comercial' },
      ] : []),

      // Cursos
      ...(isNiche(company, 'cursos') && can('alunos.access') ? [
        { id: 'alunos', label: 'Alunos', icon: BookOpen, color: 'text-blue-500', group: 'Cursos' }
      ] : []),
      ...(can('turmas.access') && (isNiche(company, 'cursos') || (Array.isArray(company?.modules_enabled) && company.modules_enabled.includes('turmas'))) ? [
        { id: 'turmas', label: 'Turmas', icon: GraduationCap, color: 'text-indigo-600', group: 'Cursos' }
      ] : []),

      // ACM
      ...(isNiche(company, 'acm') && can('estimates.access') ? [
        { id: 'estimates', label: isManager ? 'Orçamentos & Custos' : 'Histórico de Propostas', icon: FileText, color: 'text-blue-600', group: 'ACM' }
      ] : []),
      ...(isNiche(company, 'acm') && can('acm_generator.access') ? [
        { id: 'projetos-acm', label: 'Gerador ACM', icon: Hammer, color: 'text-indigo-500', group: 'ACM' }
      ] : []),
      ...(isNiche(company, 'acm') && can('producao.access') ? [
        { id: 'producao', label: 'Produção', icon: Factory, color: 'text-orange-700', group: 'ACM' }
      ] : []),

      // Financeiro
      ...(can('finance.access') ? [
        { id: 'finance', label: 'Contas a Receber', icon: DollarSign, color: 'text-emerald-700', group: 'Financeiro' }
      ] : []),
      ...(isNiche(company, 'cursos') && can('finance.access') ? [
        { id: 'eventos', label: 'Custos de Eventos', icon: Receipt, color: 'text-indigo-600', group: 'Financeiro' }
      ] : []),

      // Administração
      ...(can('users.manage') ? [
        { id: 'users', label: 'Usuários', icon: Users, color: 'text-purple-600', group: 'Administração' },
      ] : []),
      ...(can('settings.access') ? [
        { id: 'settings', label: 'Configurações', icon: Settings, color: 'text-gray-500', group: 'Administração' },
      ] : []),
      ...(isSuperAdmin ? [
        { id: 'superadmin', label: 'Admin Global', icon: ShieldAlert, color: 'text-blue-800 dark:text-blue-400', group: 'Administração' },
      ] : []),

      // Conta
      { id: 'treinamento', label: 'Treinamento', icon: PlayCircle, color: 'text-orange-600', group: 'Conta' },
      { id: 'profile', label: 'Perfil', icon: UserIcon, color: 'text-blue-500', group: 'Conta' },
    ];

    // Restrição por usuário: só os menus liberados em users.allowed_menus (+ Perfil)
    if (Array.isArray(user?.allowed_menus) && !isSuperAdmin) {
      const allowed = user.allowed_menus;
      return baseItems.filter(item => item.id === 'profile' || item.id === 'treinamento' || allowed.includes(item.id));
    }

    if (userRole === 'operacao') {
      // Dashboard da Operação é o Painel de Produção (prazos e obras paradas)
      return baseItems
        .filter(item => ['dashboard', 'leads', 'inbox', 'treinamento', 'profile'].includes(item.id))
        .map(item => item.id === 'dashboard' ? { ...item, label: 'Produção' } : item);
    }

    if (isCS) {
      return baseItems.filter(item => ['dashboard', 'turmas', 'alunos', 'leads', 'inbox', 'sales', 'treinamento', 'profile'].includes(item.id));
    }

    return baseItems.filter(item => {
      if (['dashboard', 'profile', 'treinamento', 'atendente-ia', 'superadmin', 'settings', 'users', 'finance', 'leads', 'alunos', 'inbox', 'sales', 'calendar', 'eventos', 'banco-talentos'].includes(item.id)) return true;
      if (isNiche(company, 'acm') && (item.id === 'estimates' || item.id === 'projetos-acm' || item.id === 'producao')) return true;
      if (isSuperAdmin) return true;
      if (Array.isArray(company?.modules_enabled)) {
        return company.modules_enabled.includes(item.id);
      }
      return true;
    });
  }, [userRole, isManager, isSuperAdmin, isCS, permissions, company?.id, company?.modules_enabled, company?.niche, user?.allowed_menus]);

  // Bloqueia acesso direto por URL a páginas fora de users.allowed_menus
  const isMenuRestricted = Array.isArray(user?.allowed_menus) && !isSuperAdmin;
  const currentSection = activeTab.split('/')[0] || 'dashboard';
  const isRouteAllowed = !isMenuRestricted
    || currentSection === 'profile'
    || currentSection === 'treinamento'
    || (user?.allowed_menus ?? []).includes(currentSection);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-300">
      <Topbar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        menuItems={menuItems}
      />

      <Sidebar
        user={user}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        menuItems={menuItems}
        activeTab={activeTab}
      />

      {/* Main content */}
      <div className={`transition-all duration-300 ease-in-out pt-20 print:pt-0 print:ml-0 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <div className="p-3 sm:p-6 print:p-0">
          {isRouteAllowed ? children : <Navigate to={(user?.allowed_menus ?? []).includes('dashboard') ? '/' : '/profile'} replace />}
        </div>
      </div>
      
      {!isMenuRestricted && <CommandPalette />}
      {!isMenuRestricted && <FloatingChat />}
      {!isMenuRestricted && <AssistantChat />}
      {!isMenuRestricted && <FloatingServiceNotes />}
    </div>
  );
};
